/**
 * The three settings a workspace holds as **lists** (§7.1, §5.9), which until
 * now existed in the document and nowhere on this page.
 *
 * They were left out of an organization's defaults on purpose, and that stays
 * true: an allowlist laid over every machine at once would widen what every
 * session may reach, and a host service is "the one deliberate hole in the
 * isolation boundary" — opening it for a whole organization is not a default
 * anybody should be able to set in one field. What was wrong was the other
 * half: a *workspace's* document carries all three, a member's app edits them,
 * they travel up on a publish and back down on a join — and an administrator
 * reading the same workspace in a browser could see none of it.
 *
 * So these are workspace-only rows, and they are not rows at all in the shape
 * the rest of the page uses: there is nothing above them to inherit from.
 *
 * - **The allowlist adds.** A session reaches what its machine allows *plus*
 *   what the workspace lists (`resolveSettings` is the only place in the app
 *   that merges rather than replaces), so an empty list here is not "reaches
 *   nothing", it is "adds nothing" — and the row says that rather than looking
 *   like a list somebody emptied.
 * - **The blocklist and the services have no layer above them at all.** Empty
 *   is the whole answer, and `Reset` would mean nothing.
 *
 * Every value is validated here with the same functions the app and the Worker
 * use, so a refusal is shown beside the field that caused it rather than
 * arriving as a publish that failed.
 */
import { useState } from 'react';
import type React from 'react';
import { DOMAIN, MAX_DOMAINS, MAX_HOST_SERVICES } from '@airlock/contracts/settings/primitives';
import { selectionProblem, type HostService } from '@airlock/contracts/settings/hostservice';
import { color, font, radius } from '../../theme/tokens';
import { GhostButton, Input, RowAction } from '../../components/primitives';

/**
 * The keys these editors answer for — the three the row catalogue cannot, being
 * shared with a page that must not offer them.
 *
 * Named so `settings-coverage.test.ts` can hold the whole document to account:
 * every setting a workspace carries is either a row or one of these, and a
 * fourth list arriving in the config fails that test rather than quietly
 * becoming a setting no browser can reach. Keep it in step with the blocks
 * `workspace-settings.tsx` renders.
 */
export const WORKSPACE_LIST_SETTINGS = ['extraDomains', 'blockedDomains', 'hostServices', 'localServices', 'privacyTerms'] as const;

/** A row of one of these lists: what it says, and the way to take it off. */
function Entry({
  children,
  onRemove
}: {
  children: React.ReactNode;
  onRemove?: () => void;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 0',
        boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {onRemove && (
        <RowAction danger onClick={onRemove}>
          Remove
        </RowAction>
      )}
    </div>
  );
}

const monoText: React.CSSProperties = { fontFamily: font.mono, fontSize: 12.5, color: color.inkBody };

function Problem({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p style={{ margin: '8px 0 0', fontSize: 12, color: color.badText }}>{children}</p>;
}

export function DomainList({
  value,
  onChange,
  editable,
  placeholder,
  problemOf,
  max = MAX_DOMAINS
}: {
  value: string[];
  onChange: (next: string[]) => void;
  editable?: boolean;
  placeholder: string;
  /**
   * The same list for entries that are not hosts — a login, a branch glob, a
   * check's name: why one cannot be kept, or null. Given, the entry keeps its
   * case; without it, it is a host, lower-cased and held to `DOMAIN`.
   */
  problemOf?: (entry: string) => string | null;
  max?: number;
}): React.JSX.Element {
  const [typed, setTyped] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const add = (): void => {
    const host = problemOf ? typed.trim() : typed.trim().toLowerCase();
    if (!host) return;
    const refused = problemOf
      ? problemOf(host)
      : DOMAIN.test(host)
        ? null
        : 'A hostname — letters, digits and hyphens in dotted labels, or a leading dot for a whole domain.';
    if (refused) {
      setProblem(refused);
      return;
    }
    if (value.includes(host)) {
      setProblem(`${host} is already here.`);
      return;
    }
    if (value.length >= max) {
      setProblem(problemOf ? `${max} at most.` : `A workspace lists ${MAX_DOMAINS} hosts at most.`);
      return;
    }
    onChange([...value, host]);
    setTyped('');
    setProblem(null);
  };

  return (
    <div>
      {value.map((host) => (
        <Entry key={host} onRemove={editable ? () => onChange(value.filter((one) => one !== host)) : undefined}>
          <span style={monoText}>{host}</span>
        </Entry>
      ))}
      {!value.length && (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: color.inkFaint }}>Nothing listed.</p>
      )}
      {editable && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <Input
            value={typed}
            placeholder={placeholder}
            onChange={(event) => {
              setTyped(event.target.value);
              setProblem(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              add();
            }}
            style={{ fontFamily: font.mono, fontSize: 13, maxWidth: 320 }}
          />
          <GhostButton onClick={add}>Add</GhostButton>
        </div>
      )}
      {problem && <Problem>{problem}</Problem>}
    </div>
  );
}

/** A selection as this page edits it, before it is a `HostService`. */
const BLANK = { label: '', port: '', envVar: '', url: '' };

export function HostServiceList({
  value,
  onChange,
  editable
}: {
  value: HostService[];
  onChange: (next: HostService[]) => void;
  editable?: boolean;
}): React.JSX.Element {
  const [draft, setDraft] = useState(BLANK);
  const [problem, setProblem] = useState<string | null>(null);

  const add = (): void => {
    if (value.length >= MAX_HOST_SERVICES) {
      setProblem(`A workspace opens ${MAX_HOST_SERVICES} of these at most — each one is a hole in the boundary.`);
      return;
    }
    const service: HostService = {
      // The app mints an id for a selection made on a machine; one made here is
      // named the same way — it names a row of a settings page, nothing else.
      id: `svc_${Math.random().toString(36).slice(2, 10)}`,
      label: draft.label.trim().slice(0, 80),
      port: Number(draft.port),
      envVar: draft.envVar.trim(),
      url: draft.url.trim()
    };
    const refused = selectionProblem(service);
    if (refused) {
      setProblem(refused);
      return;
    }
    onChange([...value, service]);
    setDraft(BLANK);
    setProblem(null);
  };

  const field = (
    key: keyof typeof BLANK,
    placeholder: string,
    width: number
  ): React.JSX.Element => (
    <Input
      value={draft[key]}
      placeholder={placeholder}
      onChange={(event) => {
        setDraft((current) => ({ ...current, [key]: event.target.value }));
        setProblem(null);
      }}
      style={{ fontFamily: font.mono, fontSize: 13, flex: `1 1 ${width}px`, minWidth: 0 }}
    />
  );

  return (
    <div>
      {value.map((service) => (
        <Entry key={service.id} onRemove={editable ? () => onChange(value.filter((one) => one.id !== service.id)) : undefined}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 13.5, color: color.ink }}>
              {service.label || service.envVar} · port {service.port}
            </span>
            <span style={{ ...monoText, fontSize: 11.5, color: color.inkFaint, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {service.envVar} = {service.url}
            </span>
          </span>
        </Entry>
      ))}
      {!value.length && (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: color.inkFaint }}>Nothing on the host is reachable.</p>
      )}
      {editable && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            marginTop: 10,
            padding: 10,
            border: `1px solid ${color.line}`,
            borderRadius: radius.lg
          }}
        >
          {field('label', 'Postgres', 120)}
          {field('port', '5432', 70)}
          {field('envVar', 'DATABASE_URL', 150)}
          {field('url', 'postgresql://user:pass@localhost:5432/app', 260)}
          <GhostButton onClick={add}>Add</GhostButton>
        </div>
      )}
      {problem && <Problem>{problem}</Problem>}
    </div>
  );
}

/** One of the list settings: its name, what it means, and the editor under it. */
export function ListBlock({
  label,
  hint,
  children
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ paddingTop: 20 }}>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: color.ink }}>{label}</div>
      <p style={{ margin: '4px 0 10px', fontSize: 12, lineHeight: 1.55, color: color.inkFaint, maxWidth: 640 }}>{hint}</p>
      {children}
    </div>
  );
}
